import React, { useEffect, useRef, useState, useCallback } from "react";
import Button from "./../../components/Button/Button";
import backgroundImage from "./HeaderSlider_03.jpg";
import useScrollToTop from "./../../hooks/useScollToTop";
import { getUserById, updateUserProfile } from "./../../services/userServices";
import { FaSave, FaTimes, FaCloudUploadAlt, FaImage } from "react-icons/fa";
import { getRoleFromRoleId } from "../../utils/roleHelper";
import { useUser } from "./../Home/UserContext";
import { useNotification } from "./../../components/Notification/NotificationManager";

// Modale générique
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "./../../components/Modal/Modal";

// Services photo (upload/crop + helper URL absolue)
import {
  uploadUserPhoto,
  cropUserPhoto,
  toAbsoluteUserPhotoUrl,
} from "./../../services/userPhotoServices";

// Modale de recadrage 1:1
import CropPhotoModal from "./../../components/CropPhotoModal/CropPhotoModal";

// Attestation (badge formation violences sexuelles)
import ValidationBadge from "./ValidationBadge";

/* =========================
   Avatars par défaut (optionnel)
   ========================= */
const avatars = [
  "/img/avatar/avatarF001.webp",
  "/img/avatar/avatarF002.webp",
  "/img/avatar/avatarF003.webp",
  "/img/avatar/avatarF004.webp",
  "/img/avatar/avatarF005.webp",
  "/img/avatar/avatarF006.webp",
  "/img/avatar/avatarF007.webp",
  "/img/avatar/avatarF008.webp",
  "/img/avatar/avatarM001.webp",
  "/img/avatar/avatarM002.webp",
  "/img/avatar/avatarM003.webp",
  "/img/avatar/avatarM004.webp",
  "/img/avatar/avatarM005.webp",
  "/img/avatar/avatarM006.webp",
  "/img/avatar/avatarM007.webp",
  "/img/avatar/avatarM008.webp",
  "/img/avatar/avatarM009.webp",
];

const DEFAULT_AVATAR = "/img/user_avatar.jpg";
const withBust = (u, v) =>
  !u ? u : u.includes("?") ? `${u}&v=${v}` : `${u}?v=${v}`;

// Rôles autorisés pour l’upload du badge formation
const allowedRolesForBadge = [1, 3, 4, 5, 6, 7, 8];

const Profile = () => {
  useScrollToTop();
  const { addNotification } = useNotification();
  const { user, updateUser } = useUser();

  const uid = user?.userId || user?.id; // tolérant selon ta structure
  const [coachBadgeStatus, setCoachBadgeStatus] = useState({
    printed: false,
    received: false,
  });

  const [coachBadgeStatusLoading, setCoachBadgeStatusLoading] = useState(true);

  /* =========================
     États UI
     ========================= */
  const [isEditing, setIsEditing] = useState(false);
  const [isAvatarModalOpen, setIsAvatarModalOpen] = useState(false);
  const [uploadMode, setUploadMode] = useState("avatars"); // 'avatars' | 'upload'

  // Upload fichier
  const fileInputRef = useRef(null);
  const [outputFormat, setOutputFormat] = useState("webp"); // 'webp' | 'jpg' | 'png'
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Recadrage
  const [isCropOpen, setIsCropOpen] = useState(false);

  // Preview image
  const [imgVersion, setImgVersion] = useState(0);
  const [imgOk, setImgOk] = useState(true);

  // Form profil
  const [localUser, setLocalUser] = useState({
    nom: user?.nom || "",
    prenom: user?.prenom || "",
  });

  useEffect(() => {
    if (user) {
      setLocalUser({
        nom: user.nom || "",
        prenom: user.prenom || "",
      });
      setImgOk(true);
      setImgVersion((v) => v + 1);
    }
  }, [user]);

  useEffect(() => {
    const loadCoachBadgeStatus = async () => {
      if (!uid) {
        setCoachBadgeStatusLoading(false);
        return;
      }

      try {
        setCoachBadgeStatusLoading(true);

        const freshUser = await getUserById(uid);

        if (!freshUser) {
          return;
        }

        setCoachBadgeStatus({
          printed: Boolean(freshUser.coach_badge_printed),
          received: Boolean(freshUser.coach_badge_received),
        });
      } catch (error) {
        console.error(
          "Erreur lors du chargement du statut du badge coach :",
          error,
        );
      } finally {
        setCoachBadgeStatusLoading(false);
      }
    };

    loadCoachBadgeStatus();
  }, [uid]);

  /* =========================
     Handlers Profil
     ========================= */
  const handleEdit = () => setIsEditing(true);
  const closeAllModals = () => {
    setIsEditing(false);
    setIsAvatarModalOpen(false);
    setIsUploadingFile(false);
    setUploadProgress(0);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setLocalUser((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("token");
    if (!token) {
      addNotification("Session expirée, veuillez vous reconnecter.", "error");
      return;
    }
    try {
      const updated = await updateUserProfile(uid, localUser, token);
      const normalizedUserData = {
        userId: updated.id,
        nom: updated.nom,
        prenom: updated.prenom,
        email: updated.email,
        photoURL: updated.photoURL || user?.photoURL || DEFAULT_AVATAR,
        role_id: updated.role_id,
      };
      updateUser(normalizedUserData);
      localStorage.setItem("userInfo", JSON.stringify(normalizedUserData));
      setIsEditing(false);
      addNotification("Profil mis à jour avec succès !", "success");
    } catch (error) {
      console.error(error);
      addNotification("Erreur lors de la mise à jour du profil.", "error");
    }
  };

  /* =========================
     Photo — Modale principale
     ========================= */
  const handleOpenPhotoModal = () => {
    setIsAvatarModalOpen(true);
    setUploadMode("avatars");
    setIsUploadingFile(false);
    setUploadProgress(0);
  };

  /* =========================
     Photo — Avatars par défaut
     ========================= */
  const handleAvatarSelection = async (avatarUrl) => {
    try {
      const token = localStorage.getItem("token");
      const updated = await updateUserProfile(
        uid,
        { photoURL: avatarUrl },
        token,
      );
      const normalizedUserData = {
        ...user,
        photoURL: updated.photoURL || avatarUrl,
      };
      updateUser(normalizedUserData);
      localStorage.setItem("userInfo", JSON.stringify(normalizedUserData));
      setImgOk(true);
      setImgVersion((v) => v + 1);
      addNotification("Photo de profil mise à jour avec succès !", "success");
      setIsAvatarModalOpen(false);
    } catch (error) {
      console.error(error);
      addNotification(
        "Erreur lors de la mise à jour de la photo de profil.",
        "error",
      );
    }
  };

  /* =========================
     Photo — Upload fichier (→ 400x400)
     ========================= */
  const openFilePicker = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    e.stopPropagation();
    const file = e.target.files?.[0];
    if (!file) return;
    if (!uid) {
      addNotification("ID utilisateur manquant.", "error");
      return;
    }

    try {
      setIsUploadingFile(true);
      setUploadProgress(0);

      const res = await uploadUserPhoto(uid, file, {
        format: outputFormat,
        onUploadProgress: (pe) => {
          if (!pe.total) return;
          const percent = Math.round((pe.loaded * 100) / pe.total);
          setUploadProgress(percent);
        },
      });

      const newUrl =
        res?.photo_url ||
        res?.photoURL ||
        res?.imageUrl ||
        res?.url ||
        res?.path ||
        res?.data?.photo_url ||
        res?.data?.photoURL ||
        res?.data?.imageUrl ||
        res?.data?.url ||
        res?.data?.path;

      if (newUrl) {
        const normalizedUserData = { ...user, photoURL: newUrl };
        updateUser(normalizedUserData);
        localStorage.setItem("userInfo", JSON.stringify(normalizedUserData));
        setImgOk(true);
        setImgVersion((v) => v + 1);
        addNotification("Photo uploadée et optimisée (400×400).", "success");
      } else {
        addNotification("Upload terminé mais URL non renvoyée.", "warning");
      }

      // autoriser re-sélection du même fichier
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (err) {
      console.error(err);
      addNotification(`Erreur upload: ${err?.message || "inconnue"}`, "error");
    } finally {
      setIsUploadingFile(false);
    }
  };

  /* =========================
     Photo — Recadrage serveur (ouvre AU-DESSUS)
     ========================= */
  const openCrop = useCallback(() => {
    if (!user?.photoURL) {
      addNotification("Aucune photo à recadrer.", "warning");
      return;
    }
    // ✅ Pour éviter tout chevauchement, on ferme la modale d’upload
    setIsAvatarModalOpen(false);
    // et on ouvre la modale de crop juste après (sécurité de timing)
    setTimeout(() => setIsCropOpen(true), 0);
  }, [user?.photoURL, addNotification]);

  const absolutePhoto = toAbsoluteUserPhotoUrl(user?.photoURL);
  const previewSrc = absolutePhoto
    ? withBust(absolutePhoto, imgVersion)
    : withBust(DEFAULT_AVATAR, imgVersion);

  return (
    <div
      className="min-h-screen flex items-center justify-center pt-[150px] font-satoshi"
      style={{
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div className="bg-gray-100/90 dark:bg-gray-700/80 p-4 rounded-2xl shadow-xl w-full max-w-[900px] relative ring-1 ring-slate-200/70 dark:ring-slate-800/60 backdrop-blur">
        <h1 className="text-2xl font-semibold mb-4 text-center">
          Profil Utilisateur
        </h1>

        {/* ——— Avatar + infos ——— */}
        <div className="flex flex-col items-center mb-4">
          <img
            key={imgVersion}
            src={previewSrc}
            alt="Avatar de l'utilisateur"
            className="w-32 h-32 rounded-full object-cover mb-4 ring-2 ring-white shadow-lg bg-white"
            onError={() => setImgOk(false)}
          />

          <div className="flex flex-col items-center gap-2">
            <div className="flex gap-2">
              <Button text="Modifier Photo" onClick={handleOpenPhotoModal} />
              {user?.photoURL && (
                <button
                  type="button"
                  className="h-10 rounded-xl ring-1 ring-slate-200 px-3 text-sm bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
                  onClick={openCrop}
                >
                  Recadrer / Zoom
                </button>
              )}
            </div>
            {!imgOk && (
              <div className="text-xs text-rose-600 -mt-1">
                Impossible de charger l’image.
              </div>
            )}
          </div>

          <h2 className="text-xl mt-2">
            {user?.prenom} {user?.nom}
          </h2>
          <p className="text-sm text-gray-600 dark:text-slate-200">
            Rôle : {getRoleFromRoleId(user?.role_id)}
          </p>
          <p className="text-gray-600">{user?.email}</p>
        </div>

        <div className="flex justify-center">
          <Button
            text="Modifier les informations"
            onClick={() => setIsEditing(true)}
          />
        </div>

        {/* === Module Attestation de badge (rôles autorisés) === */}
        {allowedRolesForBadge.includes(user?.role_id) && (
          <div className="mt-6 space-y-4">
            <ValidationBadge user={user} addNotification={addNotification} />

            {/* Statut administratif de la carte coach */}
            <div className="rounded-2xl border border-slate-200 bg-white/90 p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800/80">
              <div className="mb-4">
                <h3 className="text-base font-semibold text-slate-900 dark:text-white">
                  Suivi de votre badge coach
                </h3>

                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  Ces informations sont mises à jour par l'administration de la
                  Ligue.
                </p>
              </div>

              {coachBadgeStatusLoading ? (
                <div className="space-y-3">
                  <div className="h-14 animate-pulse rounded-xl bg-slate-100 dark:bg-slate-700" />
                  <div className="h-14 animate-pulse rounded-xl bg-slate-100 dark:bg-slate-700" />
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {/* Badge imprimé */}
                  <div
                    className={`flex items-center justify-between gap-4 rounded-xl border p-4 ${
                      coachBadgeStatus.printed
                        ? "border-emerald-200 bg-emerald-50 dark:border-emerald-800/60 dark:bg-emerald-950/30"
                        : "border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-900/50"
                    }`}
                  >
                    <div>
                      <p className="font-medium text-slate-900 dark:text-white">
                        Badge imprimé
                      </p>

                      <p
                        className={`mt-1 text-sm font-medium ${
                          coachBadgeStatus.printed
                            ? "text-emerald-700 dark:text-emerald-400"
                            : "text-slate-500 dark:text-slate-400"
                        }`}
                      >
                        {coachBadgeStatus.printed
                          ? "Impression effectuée"
                          : "En attente d'impression"}
                      </p>
                    </div>

                    {/* Toggle uniquement visuel */}
                    <label className="relative inline-flex cursor-default items-center">
                      <input
                        type="checkbox"
                        checked={coachBadgeStatus.printed}
                        readOnly
                        disabled
                        className="peer sr-only"
                      />

                      <div
                        className="
                  h-6 w-11 rounded-full
                  bg-slate-300
                  after:absolute
                  after:left-[2px]
                  after:top-[2px]
                  after:h-5
                  after:w-5
                  after:rounded-full
                  after:bg-white
                  after:shadow
                  after:transition-all
                  after:content-['']
                  peer-checked:bg-emerald-500
                  peer-checked:after:translate-x-full
                  dark:bg-slate-600
                "
                      />
                    </label>
                  </div>

                  {/* Badge réceptionné */}
                  <div
                    className={`flex items-center justify-between gap-4 rounded-xl border p-4 ${
                      coachBadgeStatus.received
                        ? "border-emerald-200 bg-emerald-50 dark:border-emerald-800/60 dark:bg-emerald-950/30"
                        : "border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-900/50"
                    }`}
                  >
                    <div>
                      <p className="font-medium text-slate-900 dark:text-white">
                        Badge réceptionné
                      </p>

                      <p
                        className={`mt-1 text-sm font-medium ${
                          coachBadgeStatus.received
                            ? "text-emerald-700 dark:text-emerald-400"
                            : "text-slate-500 dark:text-slate-400"
                        }`}
                      >
                        {coachBadgeStatus.received
                          ? "Badge réceptionné"
                          : "En attente de réception"}
                      </p>
                    </div>

                    {/* Toggle uniquement visuel */}
                    <label className="relative inline-flex cursor-default items-center">
                      <input
                        type="checkbox"
                        checked={coachBadgeStatus.received}
                        readOnly
                        disabled
                        className="peer sr-only"
                      />

                      <div
                        className="
                  h-6 w-11 rounded-full
                  bg-slate-300
                  after:absolute
                  after:left-[2px]
                  after:top-[2px]
                  after:h-5
                  after:w-5
                  after:rounded-full
                  after:bg-white
                  after:shadow
                  after:transition-all
                  after:content-['']
                  peer-checked:bg-emerald-500
                  peer-checked:after:translate-x-full
                  dark:bg-slate-600
                "
                      />
                    </label>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* =========================
            Modale Photo (avatars + upload)
           ========================= */}
        <Modal
          isOpen={isAvatarModalOpen}
          onClose={closeAllModals}
          size="xl"
          labelledById="photo-modal-title"
        >
          <ModalHeader
            id="photo-modal-title"
            title="Photo de Profil"
            subtitle="Choisissez un avatar ou téléversez votre photo (recommandé par la Ligue)."
            onClose={closeAllModals}
          />
          <ModalBody>
            {/* Choix du mode */}
            <div className="flex items-center gap-6 mb-4">
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="photoMode"
                  value="avatars"
                  checked={uploadMode === "avatars"}
                  onChange={() => setUploadMode("avatars")}
                />
                <span className="flex items-center gap-2 text-sm">
                  <FaImage /> Choisir un avatar
                </span>
              </label>
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="photoMode"
                  value="upload"
                  checked={uploadMode === "upload"}
                  onChange={() => setUploadMode("upload")}
                />
                <span className="flex items-center gap-2 text-sm">
                  <FaCloudUploadAlt /> Téléverser une photo
                </span>
              </label>
            </div>

            {/* Contenu : Avatars */}
            {uploadMode === "avatars" && (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-4">
                {avatars.map((avatar, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleAvatarSelection(avatar)}
                    className="group relative aspect-square rounded-2xl overflow-hidden ring-1 ring-slate-200 dark:ring-slate-700 hover:shadow transition"
                  >
                    <img
                      src={avatar}
                      alt={`Avatar ${index + 1}`}
                      className="w-full h-full object-cover group-hover:scale-105 transition"
                    />
                  </button>
                ))}
              </div>
            )}

            {/* Contenu : Upload */}
            {uploadMode === "upload" && (
              <div className="space-y-3">
                {/* INPUT FILE caché */}
                <input
                  ref={fileInputRef}
                  id="user-photo-file"
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                />

                <div className="rounded-xl border border-slate-300 p-3 bg-white/80 dark:bg-slate-900/60">
                  <div className="flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      className=" bg-primary rounded-xl gap-2 px-3 py-1.5 text-sm text-white hover:bg-primary-dark flex items-center transition ring-1 ring-primary/70 hover:ring-primary"
                      onClick={openFilePicker}
                      disabled={isUploadingFile}
                    >
                      Choisir un fichier
                    </button>

                    <div className="flex items-center gap-2">
                      <span className="text-sm">Format sortie</span>
                      <select
                        className="select select-bordered select-sm"
                        value={outputFormat}
                        onChange={(e) => setOutputFormat(e.target.value)}
                        disabled={isUploadingFile}
                      >
                        <option value="webp">WEBP (recommandé)</option>
                        <option value="jpg">JPG</option>
                        <option value="png">PNG</option>
                      </select>
                    </div>
                  </div>

                  {isUploadingFile && (
                    <div className="mt-3">
                      <progress
                        className="progress w-full"
                        value={uploadProgress}
                        max="100"
                      />
                      <div className="text-xs mt-1">{uploadProgress}%</div>
                    </div>
                  )}

                  <p className="text-xs opacity-70 mt-2">
                    La photo sera optimisée et recadrée côté serveur en carré
                    400×400. Vous pourrez ensuite l’
                    <strong>affiner avec le recadrage</strong>.
                  </p>
                </div>

                {/* Aperçu + bouton Crop (ouvre AU-DESSUS) */}
                <div>
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <FaImage />
                    <span>Aperçu actuel</span>
                  </div>
                  <div className="mt-2 h-28 w-28 rounded-xl ring-1 ring-slate-300 overflow-hidden bg-slate-100 flex items-center justify-center">
                    <img
                      key={imgVersion}
                      src={
                        user?.photoURL
                          ? withBust(
                              toAbsoluteUserPhotoUrl(user.photoURL),
                              imgVersion,
                            )
                          : withBust(DEFAULT_AVATAR, imgVersion)
                      }
                      alt="Aperçu"
                      onError={() => setImgOk(false)}
                      className="h-full w-full object-cover"
                    />
                  </div>

                  {user?.photoURL && (
                    <button
                      type="button"
                      className="mt-2 h-10 w-28 rounded-xl ring-1 ring-slate-200 overflow-hidden bg-slate-100 flex items-center justify-center text-xs"
                      onClick={openCrop}
                      disabled={isUploadingFile}
                    >
                      Recadrer / Zoom
                    </button>
                  )}

                  {!imgOk && (
                    <div className="mt-2 text-xs text-rose-600">
                      Impossible de charger l’image (vérifiez l’URL).
                    </div>
                  )}
                </div>
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              text="Fermer"
              onClick={closeAllModals}
              icon={FaTimes}
              className="!rounded-xl !bg-slate-100 !text-slate-800 hover:!bg-slate-200 dark:!bg-slate-800 dark:!text-slate-100 dark:hover:!bg-slate-700 gap-2"
            />
          </ModalFooter>
        </Modal>

        {/* =========================
            Modale Édition Profil
           ========================= */}
        <Modal
          isOpen={isEditing}
          onClose={closeAllModals}
          size="md"
          labelledById="edit-profile-title"
          describedById="edit-profile-desc"
          initialFocusSelector="#edit-nom"
        >
          <ModalHeader
            id="edit-profile-title"
            title="Modifier le Profil"
            subtitle="Mettez à jour vos informations"
            onClose={closeAllModals}
          />
          <ModalBody id="edit-profile-desc">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label
                  htmlFor="edit-nom"
                  className="text-sm font-medium text-slate-700 dark:text-slate-200"
                >
                  Nom
                </label>
                <input
                  type="text"
                  id="edit-nom"
                  name="nom"
                  value={localUser.nom}
                  onChange={handleChange}
                  data-autofocus
                  className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2"
                />
              </div>
              <div>
                <label
                  htmlFor="edit-prenom"
                  className="text-sm font-medium text-slate-700 dark:text-slate-200"
                >
                  Prénom
                </label>
                <input
                  type="text"
                  id="edit-prenom"
                  name="prenom"
                  value={localUser.prenom}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2"
                />
              </div>

              <ModalFooter>
                <Button
                  text="Annuler"
                  onClick={closeAllModals}
                  icon={FaTimes}
                  type="button"
                  className="!rounded-xl !bg-slate-100 !text-slate-800 hover:!bg-slate-200 dark:!bg-slate-800 dark:!text-slate-100 dark:hover:!bg-slate-700 gap-2"
                />
                <Button
                  text="Sauvegarder"
                  type="submit"
                  icon={FaSave}
                  className="!rounded-xl gap-2"
                />
              </ModalFooter>
            </form>
          </ModalBody>
        </Modal>

        {/* =========================
            Modale de recadrage (au-dessus)
           ========================= */}
        <CropPhotoModal
          isOpen={isCropOpen}
          onClose={() => setIsCropOpen(false)}
          entityId={uid}
          imageUrl={
            user?.photoURL
              ? `${toAbsoluteUserPhotoUrl(user.photoURL)}?v=${imgVersion}`
              : ""
          }
          onCropped={(newUrl) => {
            const normalizedUserData = { ...user, photoURL: newUrl };
            updateUser(normalizedUserData);
            localStorage.setItem(
              "userInfo",
              JSON.stringify(normalizedUserData),
            );
            setImgVersion((v) => v + 1);
            addNotification("Photo recadrée.", "success");
          }}
          format="webp"
          cropAction={cropUserPhoto}
        />
      </div>
    </div>
  );
};

export default Profile;
